import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import { showToast } from "../../../core/utils/alert";
import { City, Country, State } from "country-state-city";
import CommonSelect from "../../../core/common/commonSelect";
import { districts as jkDistricts } from "../../../core/common/selectoption/selectoption";
import { useStudent } from "../../peoples/students/hooks/useStudent";
import { API_CONFIG } from "../../../core/config";
import { decryptId } from "../../../core/utils/crypto";
import PhotoCaptureCropper from "../../../core/common/photoCaptureCropper";
import { useGetStudentByIdPublicQuery } from "../../peoples/students/api/studentApi";

interface LocationOption {
  value: string;
  label: string;
  isoCode?: string;
}

const classOptions = [
  { value: "UKG", label: "UKG" },
  { value: "1st", label: "1st" },
  { value: "2nd", label: "2nd" },
  { value: "3rd", label: "3rd" },
  { value: "4th", label: "4th" },
  { value: "5th", label: "5th" },
  { value: "6th", label: "6th" },
  { value: "7th", label: "7th" },
  { value: "8th", label: "8th" },
  { value: "9th", label: "9th" },
  { value: "10th", label: "10th" },
];

const referralOptions = [
  { value: "Friend / Family", label: "Friend / Family" },
  { value: "Social Media", label: "Social Media (Facebook / Instagram)" },
  { value: "Google / Search Engine", label: "Google / Search Engine" },
  { value: "Newspaper / Print Media", label: "Newspaper / Print Media" },
  { value: "School / Teacher", label: "School / Teacher" },
  { value: "Walk-in / Saw the Center", label: "Walk-in / Saw the Center" },
  { value: "Other", label: "Other" },
];

const CompleteRegistration = () => {
  const routes = all_routes;
  const { id: rawId } = useParams(); // Get student ID or token from URL

  const id = useMemo(() => {
    if (!rawId) return "";
    const decrypted = decryptId(rawId);
    return decrypted;
  }, [rawId]);

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    dob: "",
    fathers_name: "",
    fathers_occupation: "",
    email: "",
    phone: "",
    emergency_contact: "",
    education: "",
    institution_name: "",
    studentClass: "", // 'class' is a reserved keyword in some contexts
    aadhar_number: "",
    referral: "",
    address: {
      address: "",
      district: "",
      pincode: "",
      state: "Jammu and Kashmir",
      country: "India",
    },
    photo: null as File | null,
  });

  const { updateStudentPublic, uploadStudentPhoto } = useStudent({ skip: true });
  const { data: responseData, isLoading: isFetchingStudent, isError: isStudentError, error: studentError } = useGetStudentByIdPublicQuery({ id: id || "" }, { skip: !id });
  const studentData = responseData?.data?.student;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [countries, setCountries] = useState<LocationOption[]>([]);
  const [states, setStates] = useState<LocationOption[]>([]);
  const [districts, setDistricts] = useState<LocationOption[]>([]);

  // Load countries from installed country-state-city package
  useEffect(() => {
    const countryOptions = Country.getAllCountries().map((country) => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode,
    }));
    setCountries(countryOptions);
  }, []);

  // Pre-fill existing fields
  useEffect(() => {
    if (studentData) {
      setFormData({
        name: studentData.name || "",
        gender: studentData.profile?.gender || "",
        dob: studentData.profile?.dob || "",
        fathers_name: studentData.profile?.fathers_name || "",
        fathers_occupation: studentData.profile?.fathers_occupation || "",
        email: studentData.profile?.email || "",
        phone: studentData.profile?.phone?.toString() || "",
        emergency_contact: studentData.profile?.emergency_contact && studentData.profile.emergency_contact !== 0 && String(studentData.profile.emergency_contact) !== "0" ? studentData.profile.emergency_contact.toString() : "",
        education: studentData.profile?.education || "",
        institution_name: studentData.profile?.institution_name || "",
        studentClass: studentData.profile?.class || "",
        aadhar_number: studentData.profile?.aadhar_number && studentData.profile.aadhar_number !== 0 && String(studentData.profile.aadhar_number) !== "0" ? studentData.profile.aadhar_number.toString() : "",
        referral: studentData.profile?.referral || "",
        address: {
          address: studentData.address?.address || "",
          district: studentData.address?.district || "",
          pincode: studentData.address?.pincode?.toString() || "",
          state: studentData.address?.state || "Jammu and Kashmir",
          country: studentData.address?.country || "India",
        },
        photo: null,
      });

      if (studentData.docs?.photo) {
        setPreviewImage(`${API_CONFIG.BASE_URL}students/get-thumbnail/${studentData.docs.photo}`);
      }
    }
  }, [studentData]);

  // Load states when country changes
  useEffect(() => {
    if (!formData.address.country) {
      setStates([]);
      return;
    }

    const selectedCountry = Country.getAllCountries().find(
      (c) => c.name === formData.address.country
    );

    if (selectedCountry) {
      const stateOptions = State.getStatesOfCountry(selectedCountry.isoCode).map(
        (state) => ({
          value: state.name,
          label: state.name,
          isoCode: state.isoCode,
        })
      );
      setStates(stateOptions);
    }
  }, [formData.address.country]);

  // Load districts/cities when state changes
  useEffect(() => {
    if (!formData.address.state || !formData.address.country) {
      setDistricts([]);
      return;
    }

    if (formData.address.state === "Jammu and Kashmir") {
      setDistricts(jkDistricts);
      return;
    }

    const selectedCountry = Country.getAllCountries().find(
      (c) => c.name === formData.address.country
    );
    if (!selectedCountry) return;

    const selectedState = State.getStatesOfCountry(selectedCountry.isoCode).find(
      (s) => s.name === formData.address.state
    );

    if (selectedCountry && selectedState) {
      const cityOptions = City.getCitiesOfState(
        selectedCountry.isoCode,
        selectedState.isoCode
      ).map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setDistricts(cityOptions);
    }
  }, [formData.address.state, formData.address.country]);

  // Capitalize first letter of each word
  const capitalizeWords = (str: string): string => {
    return str.replace(/(^|\s)\S/g, (char) => char.toUpperCase());
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let { name, value } = e.target;

    // Auto-capitalize first letter of each word for personal details & institution & address
    if (["name", "fathers_name", "fathers_occupation", "institution_name", "address.address"].includes(name)) {
      value = capitalizeWords(value);
    }

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value,
        },
      });
      setErrors({ ...errors, [name]: "" });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        [field]: value,
      },
    });
    setErrors({ ...errors, [`address.${field}`]: "" });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFormData({ ...formData, photo: file });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0); // Scroll to top of form on mobile
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0); // Scroll to top of form on mobile
    }
  };

  const steps = [
    { title: "Personal", icon: "ti-user" },
    { title: "Contact", icon: "ti-phone" },
    { title: "Education", icon: "ti-book" },
    { title: "Address", icon: "ti-map-pin" },
  ];

  const isStepComplete = () => {
    if (currentStep === 1) {
      return !!(
        formData.name.trim() &&
        formData.dob &&
        formData.gender &&
        formData.fathers_name.trim() &&
        formData.fathers_occupation.trim()
      );
    }
    if (currentStep === 2) {
      return !!(
        formData.phone.trim()
      );
    }
    if (currentStep === 3) {
      return !!(
        formData.studentClass &&
        formData.institution_name.trim()
      );
    }
    if (currentStep === 4) {
      return !!(
        formData.address.address.trim() &&
        formData.address.country &&
        formData.address.state &&
        formData.address.district &&
        formData.address.pincode.trim()
      );
    }
    return false;
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    if (currentStep === 1) {
      if (!formData.photo && !studentData?.docs?.photo) {
        newErrors.photo = "Photograph is required";
      }
      if (!formData.name.trim()) newErrors.name = "Full name is required";
      if (!formData.dob) {
        newErrors.dob = "Date of Birth is required";
      } else {
        const birthDate = new Date(formData.dob);
        const today = new Date();
        
        const minimumDate = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());
        const maximumDate = new Date(today.getFullYear() - 3, today.getMonth(), today.getDate());

        if (birthDate > maximumDate) {
          const formattedMax = `${String(maximumDate.getDate()).padStart(2, '0')}-${String(maximumDate.getMonth() + 1).padStart(2, '0')}-${maximumDate.getFullYear()}`;
          newErrors.dob = `Student must be at least 3 years old (born on or before ${formattedMax})`;
        } else if (birthDate < minimumDate) {
          const formattedMin = `${String(minimumDate.getDate()).padStart(2, '0')}-${String(minimumDate.getMonth() + 1).padStart(2, '0')}-${minimumDate.getFullYear()}`;
          newErrors.dob = `Student age cannot exceed 16 years (born on or after ${formattedMin})`;
        }
      }
      if (!formData.gender) newErrors.gender = "Gender is required";
      if (!formData.fathers_name.trim()) newErrors.fathers_name = "Father's name is required";
      if (!formData.fathers_occupation.trim()) newErrors.fathers_occupation = "Father's occupation is required";
      if (formData.aadhar_number.trim() && !/^\d{12}$/.test(formData.aadhar_number.trim())) {
        newErrors.aadhar_number = "Aadhar number must be 12 digits";
      }
    }
    if (currentStep === 2) {
      if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
      if (formData.emergency_contact.trim()) {
        if (!/^\d{10}$/.test(formData.emergency_contact.trim())) {
          newErrors.emergency_contact = "Emergency contact must be 10 digits";
        }
      }
      if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
        newErrors.email = "Invalid email format";
      }
    }
    if (currentStep === 3) {
      if (!formData.studentClass) newErrors.studentClass = "Class is required";
      if (!formData.institution_name.trim()) newErrors.institution_name = "Institution name is required";
    }
    if (currentStep === 4) {
      if (!formData.address.address.trim()) newErrors["address.address"] = "Address is required";
      if (!formData.address.country) newErrors["address.country"] = "Country is required";
      if (!formData.address.state) newErrors["address.state"] = "State is required";
      if (!formData.address.district) newErrors["address.district"] = "District is required";
      if (!formData.address.pincode.trim()) {
        newErrors["address.pincode"] = "Pincode is required";
      } else if (!/^\d{6}$/.test(formData.address.pincode.trim())) {
        newErrors["address.pincode"] = "Pincode must be 6 digits";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) {
      return;
    }

    if (currentStep !== totalSteps) {
      nextStep();
      return;
    }

    setIsLoading(true);

    try {
      if (id && studentData) {
        const payload = {
          name: formData.name,
          profile: {
            gender: formData.gender,
            dob: formData.dob,
            fathers_name: formData.fathers_name,
            fathers_occupation: formData.fathers_occupation,
            email: formData.email?.trim() || undefined,
            phone: formData.phone,
            emergency_contact: formData.emergency_contact?.trim() || undefined,
            education: formData.education,
            institution_name: formData.institution_name,
            class: formData.studentClass,
            aadhar_number: formData.aadhar_number?.trim() || undefined,
            referral: formData.referral?.trim() || undefined,
          },
          address: formData.address,
          center_id: studentData?.center?.id || 0,
          course_id: studentData?.course?.id || 0,
          stage_id: studentData?.course?.stage?.id || 0,
          session_id: studentData?.session?.id || 0,
          status: studentData?.status || "active",
          batch_id: studentData?.batch?.id || null,
          reg_status: "completed",
        };

        const result = await updateStudentPublic(id, payload as any);

        if (formData.photo && result.success) {
          await uploadStudentPhoto(id, formData.photo);
        }

        if (result.success) {
          setIsLoading(false);
          setIsSubmitted(true);
          showToast({
            title: "Success",
            text: "Registration completed successfully!",
            icon: "success",
          });
        } else {
          throw new Error(result.error);
        }
      } else {
        throw new Error("Student data not found.");
      }
    } catch (error) {
      setIsLoading(false);
      showToast({
        title: "Error",
        text: "Failed to complete registration.",
        icon: "error",
      });
    }
  };

  // Helper function to render step indicators
  const renderStepIndicator = (stepNum: number, title: string, icon: string) => {
    const isActive = currentStep === stepNum;
    const isCompleted = currentStep > stepNum;
    return (
      <div className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 1 }}>
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center mb-1 mb-md-2 shadow-sm ${isActive ? 'bg-primary text-white' : isCompleted ? 'bg-success text-white' : 'bg-light text-muted'}`}
          style={{ width: "55px", height: "55px", transition: "all 0.3s ease" }}
        >
          {isCompleted ? <i className="ti ti-check fs-3"></i> : <i className={`ti ${icon} fs-3`}></i>}
        </div>
        <span className={`small fw-semibold d-none d-sm-block mt-2 ${isActive || isCompleted ? 'text-dark' : 'text-muted'}`}>{title}</span>
      </div>
    );
  };

  if (!id) {
    return <Navigate to={routes.error401} state={{ hideBtn: true }} replace />;
  }

  return (
    <>
      <div className="container-fluid bg-light-300 min-vh-100 d-flex flex-column px-2 px-md-4">
        <div className="w-100 position-relative flex-wrap d-block py-2 py-md-3">
          <div className="row justify-content-center align-items-center m-0">
            <div className="col-lg-8 col-md-10 col-12 p-0 p-md-2">
              <div className="card shadow-none border-0 bg-transparent rounded-3">
                <div className="card-body p-1 p-md-3">
                  {studentData?.reg_status === "completed" && !isSubmitted ? (
                    <div className="text-center py-5 animation-fade-in">
                      <div
                        className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center mb-4 shadow"
                        style={{ width: "80px", height: "80px" }}
                      >
                        <i className="ti ti-check" style={{ fontSize: "3rem" }}></i>
                      </div>
                      <h2 className="fw-bold text-dark mb-4">Registration Already Complete</h2>
                      <p className="text-dark mb-4 fs-3 px-md-5">
                        Your registration details have already been submitted successfully.
                      </p>
                      <p className="text-muted fs-4">
                        You may safely close this window.
                      </p>
                    </div>
                  ) : isSubmitted ? (
                    <div className="text-center py-5 animation-fade-in">
                      <div
                        className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center mb-4 shadow"
                        style={{ width: "80px", height: "80px" }}
                      >
                        <i className="ti ti-check" style={{ fontSize: "3rem" }}></i>
                      </div>
                      <h2 className="fw-bold text-dark mb-4">Registration Complete!</h2>
                      <p className="text-dark mb-4 fs-3 px-md-5">
                        Thank you for submitting your details. Your profile has been updated successfully.
                      </p>
                      <p className="text-muted fs-4">
                        You may now safely close this window.
                      </p>
                    </div>
                  ) : isStudentError ? (
                    <div className="text-center py-5 animation-fade-in">
                      <div
                        className="rounded-circle bg-danger text-white d-inline-flex align-items-center justify-content-center mb-4 shadow"
                        style={{ width: "80px", height: "80px" }}
                      >
                        <i className="ti ti-x" style={{ fontSize: "3rem" }}></i>
                      </div>
                      <h2 className="fw-bold text-dark mb-4">Link Invalid or Expired</h2>
                      <p className="text-dark mb-4 fs-3 px-md-5">
                        We couldn't find the registration details for this link. It may have been deleted or expired.
                      </p>
                      <p className="text-muted fs-4">
                        Please contact the administration for a new link.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mb-4">
                        <ImageWithBasePath
                          src="assets/img/authentication/authentication-logo.png"
                          className="img-fluid mb-3 w-50"
                          alt="Logo"
                        />
                        <h3 className="mb-2 fw-bold text-dark fs-4 fs-md-3">Complete Your Registration</h3>
                        <p className="text-muted small">
                          Please verify and complete all the required details to finalize your profile.
                        </p>
                      </div>

                      {/* Step Progress Bar */}
                      <div className="position-relative mb-3 px-1 px-md-3">
                        <div className="progress position-absolute" style={{ top: "27px", left: "10%", right: "10%", height: "4px", zIndex: 0 }}>
                          <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`, transition: "width 0.4s ease" }}
                            aria-valuenow={((currentStep - 1) / (totalSteps - 1)) * 100}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          ></div>
                        </div>
                        <div className="d-flex justify-content-between">
                          {steps.map((step, index) => (
                            <div key={index} className="d-flex flex-column align-items-center">
                              {renderStepIndicator(index + 1, step.title, step.icon)}
                            </div>
                          ))}
                        </div>
                      </div>

                      <form onSubmit={handleSubmit} noValidate>

                        {/* STEP 1: Personal Details */}
                        {currentStep === 1 && (
                          <div className="step-content animation-fade-in">
                            <PhotoCaptureCropper
                              photo={formData.photo}
                              existingPhotoUrl={
                                studentData?.docs?.photo
                                  ? `${API_CONFIG.BASE_URL}students/get-thumbnail/${studentData.docs.photo}?v=${Date.now()}`
                                  : null
                              }
                              disabled={!!studentData?.docs?.photo}
                              onPhotoChange={(file) => {
                                setFormData({ ...formData, photo: file });
                                setErrors({ ...errors, photo: "" });
                              }}
                              error={errors.photo}
                              required={true}
                            />
                            <div className="row">
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Full Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  name="name"
                                  value={formData.name}
                                  onChange={handleInputChange}
                                  placeholder="Enter your full name"
                                  disabled={!!studentData?.name}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                                <input
                                  type="date"
                                  className={`form-control ${errors.dob ? "is-invalid" : ""}`}
                                  name="dob"
                                  value={formData.dob}
                                  onChange={handleInputChange}
                                  disabled={!!studentData?.profile?.dob}
                                  min={(() => {
                                    const d = new Date();
                                    d.setFullYear(d.getFullYear() - 16);
                                    return d.toISOString().split("T")[0];
                                  })()}
                                  max={(() => {
                                    const d = new Date();
                                    d.setFullYear(d.getFullYear() - 3);
                                    return d.toISOString().split("T")[0];
                                  })()}
                                />
                                {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Gender <span className="text-danger">*</span></label>
                                <select
                                  className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                                  name="gender"
                                  value={formData.gender}
                                  onChange={handleInputChange}
                                  disabled={!!studentData?.profile?.gender}
                                >
                                  <option value="">Select Gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                </select>
                                {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Father's Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.fathers_name ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  name="fathers_name"
                                  value={formData.fathers_name}
                                  onChange={handleInputChange}
                                  placeholder="Enter father's name"
                                  disabled={!!studentData?.profile?.fathers_name}
                                />
                                {errors.fathers_name && <div className="invalid-feedback">{errors.fathers_name}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Father's Occupation <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.fathers_occupation ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  name="fathers_occupation"
                                  value={formData.fathers_occupation}
                                  onChange={handleInputChange}
                                  placeholder="Enter father's occupation"
                                  disabled={!!studentData?.profile?.fathers_occupation}
                                />
                                {errors.fathers_occupation && <div className="invalid-feedback">{errors.fathers_occupation}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Aadhar Number</label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.aadhar_number ? "is-invalid" : ""}`}
                                  name="aadhar_number"
                                  value={formData.aadhar_number}
                                  onChange={handleInputChange}
                                  placeholder="Enter 12-digit Aadhar number"
                                  disabled={!!studentData?.profile?.aadhar_number && studentData.profile.aadhar_number !== 0}
                                />
                                {errors.aadhar_number && <div className="invalid-feedback">{errors.aadhar_number}</div>}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* STEP 2: Contact Details */}
                        {currentStep === 2 && (
                          <div className="step-content animation-fade-in">
                            <div className="row">
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                                  name="phone"
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                  placeholder="Enter primary phone number"
                                  disabled={!!studentData?.profile?.phone}
                                />
                                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Emergency Contact</label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.emergency_contact ? "is-invalid" : ""}`}
                                  name="emergency_contact"
                                  value={formData.emergency_contact}
                                  onChange={handleInputChange}
                                  placeholder="Enter emergency contact number"
                                  disabled={!!studentData?.profile?.emergency_contact && studentData.profile.emergency_contact !== 0}
                                />
                                {errors.emergency_contact && <div className="invalid-feedback">{errors.emergency_contact}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Email Address</label>
                                <input
                                  type="email"
                                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                                  name="email"
                                  value={formData.email}
                                  onChange={handleInputChange}
                                  placeholder="Enter your email"
                                  disabled={!!studentData?.profile?.email}
                                />
                                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">How did you hear about us? <span className="text-muted small">(Optional)</span></label>
                                <CommonSelect
                                  className={`select ${errors.referral ? "is-invalid" : ""}`}
                                  options={referralOptions}
                                  value={referralOptions.find(
                                    (option) => option.value === formData.referral
                                  ) || null}
                                  onChange={(value: any) => {
                                    setFormData({ ...formData, referral: value?.value || "" });
                                    setErrors({ ...errors, referral: "" });
                                  }}
                                  placeholder="Select how you heard about us"
                                  isDisabled={!!studentData?.profile?.referral}
                                />
                                {errors.referral && <div className="text-danger small mt-1">{errors.referral}</div>}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* STEP 3: Educational Details */}
                        {currentStep === 3 && (
                          <div className="step-content animation-fade-in">
                            <div className="row">
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Class <span className="text-danger">*</span></label>
                                <CommonSelect
                                  className={`select ${errors.studentClass ? "is-invalid" : ""}`}
                                  options={classOptions}
                                  value={classOptions.find(
                                    (option) => option.value === formData.studentClass
                                  )}
                                  onChange={(value: any) => {
                                    setFormData({ ...formData, studentClass: value?.value || "" });
                                    setErrors({ ...errors, studentClass: "" });
                                  }}
                                  placeholder="Select Class"
                                  isDisabled={!!studentData?.profile?.class}
                                />
                                {errors.studentClass && <div className="text-danger small mt-1">{errors.studentClass}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">School / Institution Name <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors.institution_name ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  name="institution_name"
                                  value={formData.institution_name}
                                  onChange={handleInputChange}
                                  placeholder="Enter school/college name"
                                  disabled={!!studentData?.profile?.institution_name}
                                />
                                {errors.institution_name && <div className="invalid-feedback">{errors.institution_name}</div>}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* STEP 4: Address Details */}
                        {currentStep === 4 && (
                          <div className="step-content animation-fade-in">
                            <div className="row">
                              <div className="col-12 mb-3">
                                <label className="form-label">Full Address <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors["address.address"] ? "is-invalid" : ""}`}
                                  style={{ textTransform: "capitalize" }}
                                  name="address.address"
                                  value={formData.address.address}
                                  onChange={handleInputChange}
                                  placeholder="Enter street, area, landmark"
                                  disabled={!!studentData?.address?.address}
                                />
                                {errors["address.address"] && <div className="invalid-feedback">{errors["address.address"]}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Country <span className="text-danger">*</span></label>
                                <CommonSelect
                                  className={`select ${errors["address.country"] ? "is-invalid" : ""}`}
                                  options={countries}
                                  value={countries.find(
                                    (option) => option.value === formData.address.country
                                  )}
                                  onChange={(value: any) => {
                                    handleSelectChange("country", value?.value || "");
                                    handleSelectChange("state", "");
                                    handleSelectChange("district", "");
                                  }}
                                  placeholder="Select Country"
                                  isDisabled={!!studentData?.address?.country}
                                />
                                {errors["address.country"] && <div className="text-danger small mt-1">{errors["address.country"]}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">State <span className="text-danger">*</span></label>
                                <CommonSelect
                                  className={`select ${errors["address.state"] ? "is-invalid" : ""}`}
                                  options={states}
                                  value={states.find(
                                    (option) => option.value === formData.address.state
                                  )}
                                  onChange={(value: any) => {
                                    handleSelectChange("state", value?.value || "");
                                    handleSelectChange("district", "");
                                  }}
                                  isDisabled={!!studentData?.address?.state}
                                  placeholder="Select State"
                                />
                                {errors["address.state"] && <div className="text-danger small mt-1">{errors["address.state"]}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">District <span className="text-danger">*</span></label>
                                <CommonSelect
                                  className={`select ${errors["address.district"] ? "is-invalid" : ""}`}
                                  options={districts}
                                  value={districts.find(
                                    (option) => option.value === formData.address.district
                                  )}
                                  onChange={(value: any) => {
                                    handleSelectChange("district", value?.value || "");
                                  }}
                                  isDisabled={!!studentData?.address?.district}
                                  placeholder="Select District"
                                />
                                {errors["address.district"] && <div className="text-danger small mt-1">{errors["address.district"]}</div>}
                              </div>
                              <div className="col-12 col-md-6 mb-3">
                                <label className="form-label">Pincode <span className="text-danger">*</span></label>
                                <input
                                  type="text"
                                  className={`form-control ${errors["address.pincode"] ? "is-invalid" : ""}`}
                                  name="address.pincode"
                                  value={formData.address.pincode}
                                  onChange={handleInputChange}
                                  placeholder="Enter pincode"
                                  disabled={!!studentData?.address?.pincode}
                                />
                                {errors["address.pincode"] && <div className="invalid-feedback">{errors["address.pincode"]}</div>}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className={`d-flex mt-4 pt-3 border-top gap-2 ${currentStep === 1 ? 'justify-content-end' : 'justify-content-between'}`}>
                          {currentStep > 1 && (
                            <button
                              type="button"
                              className="btn btn-outline-secondary px-3 px-md-4 py-2 fw-semibold w-100 w-sm-auto"
                              onClick={prevStep}
                              disabled={isLoading}
                              style={{ maxWidth: "150px" }}
                            >
                              <i className="ti ti-arrow-left me-1"></i> Prev
                            </button>
                          )}

                          {currentStep < totalSteps ? (
                            <button
                              type="submit"
                              className="btn btn-primary px-3 px-md-4 py-2 fw-semibold w-100 w-sm-auto"
                              style={{ maxWidth: "150px" }}
                              disabled={!isStepComplete()}
                            >
                              Next <i className="ti ti-arrow-right ms-1"></i>
                            </button>
                          ) : (
                            <button
                              type="submit"
                              className="btn btn-success px-3 px-md-5 py-2 fw-bold shadow w-100 w-sm-auto"
                              disabled={isLoading || !isStepComplete()}
                            >
                              {isLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Submitting...
                                </>
                              ) : (
                                <>Submit <i className="ti ti-check ms-1 d-none d-sm-inline"></i></>
                              )}
                            </button>
                          )}
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="mb-0 text-muted small">
                  Copyright © {new Date().getFullYear()} - Aloha. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simple fade-in animation style */}
      <style>{`
        .animation-fade-in {
          animation: fadeIn 0.4s ease-in-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default CompleteRegistration;
